import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Modal,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrintIcon from "@mui/icons-material/Print";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarMenu from "../../Administrador/AdminPanel/SidebarMenu";
import TopBar from "../../Administrador/AdminPanel/TopBar";
import InfoInvoice from "./InfoInvoice";
import InvoiceDetail from "./InvoiceDetail";

const getCSRF = () =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1] || "";

const NewInvoice = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [invoiceInfo, setInvoiceInfo] = useState({});
  const [productRows, setProductRows] = useState([]);
  const [productDescripcion, setProductDescripcion] = useState("");
  const [invoiceData, setInvoiceData] = useState({});
  const [payments, setPayments] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState("loading");
  const [modalMessage, setModalMessage] = useState("");
  const [successSaved, setSuccessSaved] = useState(false);
  const [documentoId, setDocumentoId] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState("");

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailDocNumero, setEmailDocNumero] = useState("");
  const [emailRazon, setEmailRazon] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const navigate = useNavigate();
  const location = useLocation();

  const prefillOnceRef = useRef(false);

  const isAuthorized = (invoiceStatus || "").toUpperCase() === "AUTORIZADO";

  useEffect(() => {
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";
    if (!isSuper && !isAdmin && !isVendedor) navigate("/login");
  }, [location, navigate]);

  useEffect(() => {
    if (prefillOnceRef.current) return;
    const prefill = location?.state?.prefill_invoice;
    if (!prefill) return;

    prefillOnceRef.current = true;

    const info = prefill.info || prefill.invoiceInfo || prefill.cliente || {};
    const rows =
      prefill.productRows ||
      prefill.productos_rows ||
      prefill.productos_raw ||
      prefill.rows ||
      prefill.productos ||
      [];
    const desc =
      prefill.descripcion_factura ||
      prefill.descripcion ||
      prefill.productDescripcion ||
      "";
    const pay =
      prefill.pagos ||
      prefill.payments ||
      [];

    setInvoiceInfo(info || {});
    setProductRows(Array.isArray(rows) ? rows : []);
    setProductDescripcion(String(desc || ""));
    setPayments(Array.isArray(pay) ? pay : []);
  }, [location]);

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSave = async () => {
    if (documentoId) {
      setSnack({
        open: true,
        sev: "info",
        msg:
          "Este documento ya fue guardado. No vuelvas a presionar Guardar. Usa “Ver Documento”. El sistema maneja reenvío porque el SRI a veces no responde.",
      });
      return;
    }

    setModalState("loading");
    setModalMessage("Procesando documento...");
    setModalOpen(true);
    setSuccessSaved(false);
    setDocumentoId(null);
    setInvoiceStatus("");

    const payload = {
      info: { ...invoiceInfo, descripcion_factura: productDescripcion },
      productos: invoiceData,
      pagos: Array.isArray(payments) ? payments : [],
    };

    try {
      const res = await fetch("/facturas/documento/crear/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setModalState("error");
        setModalMessage(data.error || "No fue posible crear el documento.");
        return;
      }

      const docId = data.documento_id || null;
      setDocumentoId(docId);

      const estado = (data.estado || "").toUpperCase();
      setInvoiceStatus(estado);

      if (docId) setSuccessSaved(true);

      if (estado === "AUTORIZADO") {
        setModalState("success");
        setModalMessage(
          "La factura fue autorizada correctamente por el SRI y tiene validez tributaria."
        );
        return;
      }

      if (estado === "NO_AUTORIZADO") {
        setModalState("error");
        setModalMessage(
          (data.sri_mensaje || "El SRI rechazó el documento.") +
            "\n\n⚠️ No presiones Guardar nuevamente: el sistema maneja reenvío de documentos porque el SRI a veces no responde. Revisa “Ver Documento”."
        );
        return;
      }

      if (estado === "ERROR") {
        setModalState("error");
        setModalMessage(
          (data.sri_mensaje || "Ocurrió un error al firmar o enviar el documento.") +
            "\n\n⚠️ No presiones Guardar nuevamente: el sistema maneja reenvío de documentos porque el SRI a veces no responde. Revisa “Ver Documento”."
        );
        return;
      }

      setModalState("info");
      setModalMessage(
        (data.sri_mensaje ||
          "El documento fue creado y quedó en proceso (el SRI puede tardar o no responder).") +
          "\n\n✅ No presiones Guardar nuevamente: el sistema maneja reenvío de documentos porque el SRI a veces no responde. Revisa “Ver Documento”."
      );
    } catch {
      setModalState("error");
      setModalMessage("Error de comunicación con el servidor.");
    }
  };

  const descargarArchivo = async (url) => {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const disposition = resp.headers.get("Content-Disposition") || "";
    let filename = "documento.pdf";
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) filename = match[1];
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openEmailModal = async () => {
    if (!documentoId) return;

    setEmailOpen(true);
    setEmailLoading(true);
    setEmailError("");
    setEmailDocNumero("");
    setEmailRazon("");
    setEmailValue("");

    try {
      await fetch("/facturas/csrf/", { credentials: "include" });

      const r = await fetch("/facturas/enviar/info/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify({ documento_id: documentoId }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        throw new Error(data.error || "No se pudo cargar la información de envío");
      }

      setEmailDocNumero(data.documento || "");
      setEmailRazon(data.razon_social || "");
      setEmailValue(data.correo || "");
    } catch (e) {
      setEmailError(e?.message || "Error al cargar información");
    } finally {
      setEmailLoading(false);
    }
  };

  const validateEmail = (v) => {
    const s = (v || "").trim();
    if (!s) return "Correo requerido";
    if (!s.includes("@")) return "Correo inválido";
    return "";
  };

  const sendEmail = async () => {
    const err = validateEmail(emailValue);
    setEmailError(err);
    if (err) return;
    if (!documentoId) return;

    setEmailSending(true);
    try {
      await fetch("/facturas/csrf/", { credentials: "include" });

      const r = await fetch("/facturas/enviar/enviar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        },
        body: JSON.stringify({ documento_id: documentoId, correo: emailValue.trim() }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        throw new Error(data.error || "No se pudo enviar el correo");
      }

      setSnack({ open: true, msg: "Correo enviado correctamente", sev: "success" });
      setEmailOpen(false);
    } catch (e) {
      setSnack({ open: true, msg: e?.message || "Error al enviar correo", sev: "error" });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", background: "#f5f6fa", minHeight: "100vh" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1565C0", mb: 2 }}>
              Registrar Documento Electrónico
            </Typography>

            {documentoId && !isAuthorized && (
              <Alert severity="info" sx={{ mb: 2 }}>
                ✅ Documento guardado. <b>No</b> presiones “Guardar” nuevamente: el sistema maneja <b>reenvío</b> de documentos
                porque el SRI a veces no responde. Usa “Ver Documento”.
              </Alert>
            )}

            <InfoInvoice onChange={setInvoiceInfo} initialData={invoiceInfo} />

            <InvoiceDetail
              invoiceData={invoiceData}
              onInvoiceDataChange={setInvoiceData}
              payments={payments}
              setPayments={setPayments}
              productRows={productRows}
              setProductRows={setProductRows}
              productDescripcion={productDescripcion}
              setProductDescripcion={setProductDescripcion}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, flexWrap: "wrap" }}>
              {documentoId ? (
                isAuthorized ? (
                  <>
                    <Button
                      startIcon={<MailOutlineIcon />}
                      variant="contained"
                      sx={{ bgcolor: "#0870A2" }}
                      onClick={openEmailModal}
                    >
                      Enviar
                    </Button>

                    <Button
                      startIcon={<PrintIcon />}
                      variant="contained"
                      sx={{ bgcolor: "#0870A2" }}
                      onClick={() => descargarArchivo(`/facturas/pdf/${documentoId}/`)}
                    >
                      Imprimir
                    </Button>

                    <Button
                      startIcon={<ConfirmationNumberIcon />}
                      variant="contained"
                      sx={{ bgcolor: "#0870A2" }}
                      onClick={() => descargarArchivo(`/facturas/ticket/${documentoId}/`)}
                    >
                      Ticket
                    </Button>

                    <Button
                      variant="contained"
                      sx={{ bgcolor: "#1565C0" }}
                      onClick={() => navigate(`/admin-panel/factura/V-${documentoId}`)}
                    >
                      Ver Factura
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "#1565C0" }}
                    onClick={() => navigate(`/admin-panel/factura/V-${documentoId}`)}
                  >
                    Ver Documento
                  </Button>
                )
              ) : (
                <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={handleSave}>
                  Guardar
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      <Modal open={modalOpen} onClose={modalState === "loading" ? null : () => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 4,
            borderRadius: 2,
            width: 520,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {modalState === "loading" && (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography>{modalMessage}</Typography>
            </>
          )}

          {modalState === "success" && (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "green", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Aceptar
              </Button>
            </>
          )}

          {modalState === "info" && (
            <>
              <InfoOutlinedIcon sx={{ fontSize: 60, color: "#f59e0b", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Entendido
              </Button>
            </>
          )}

          {modalState === "error" && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 60, color: "#d32f2f", mb: 2 }} />
              <Typography sx={{ mb: 3 }}>{modalMessage}</Typography>
              <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => setModalOpen(false)}>
                Entendido
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Dialog open={emailOpen} onClose={emailSending ? undefined : () => setEmailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Enviar factura por correo</DialogTitle>
        <DialogContent dividers>
          {emailLoading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando información…</Typography>
            </Box>
          ) : (
            <>
              {emailError ? (
                <Alert severity="error">{emailError}</Alert>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Se enviará la factura {emailDocNumero || "-"}
                  </Typography>
                  <Typography sx={{ color: "#555", mb: 2 }}>{emailRazon || "-"}</Typography>

                  <Divider sx={{ mb: 2 }} />

                  <TextField
                    label="Correo de destino"
                    value={emailValue}
                    onChange={(e) => {
                      setEmailValue(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    fullWidth
                    error={Boolean(emailError)}
                    helperText={emailError || "Verifica el correo antes de enviar"}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEmailOpen(false)} disabled={emailSending} sx={{ fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={sendEmail}
            disabled={emailLoading || emailSending || !emailValue.trim() || Boolean(emailError)}
            sx={{ bgcolor: "#1565C0", fontWeight: 800 }}
          >
            {emailSending ? "Enviando…" : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewInvoice;
